import { designVariables } from "@studio3t/design-variables";
import {
  ButtonMediumPrimaryLeadingIcon,
  GenericDropdown,
  GenericDropdownContext,
  type GenericDropdownItem,
  Icon,
  IconSVG,
  SVGColorKey,
  Tooltip,
} from "@studio3t/generic-components";
import styled, { css, useTheme } from "styled-components";
import { type DeployProblem, onlyChecking } from "./deployProblems";

// The header's Deploy control. Clean: the primary "Deploy pipeline"
// button. With problems: the SegmentedButtonGroup of Bridge_PageTitle
// 15031-152458 — Deploy disabled, plus an attached "Unresolved (N)"
// trigger (error icon, error-coloured border) whose menu (Figma
// DropdownMenu 14669-190836) lists every editor problem; a row takes the
// user to the editor to fix it. Problems that are just checks still
// settling (lint running, services loading — every open passes through a
// beat of them) gate Deploy without the error chrome: a disabled button
// with the reason as its tooltip, the toolbar Run's recipe.

const BUTTON = designVariables.ui.button;
const SINGLE_LINE = designVariables.type.singleLine;
const MENU_WIDTH = 320;

function problemItems(
  problems: readonly DeployProblem[],
  onGoTo: (problem: DeployProblem) => void,
): GenericDropdownItem[] {
  return [
    problems.map((problem) => ({
      id: problem.id,
      type: "text" as const,
      value: problem.id,
      label: problem.label,
      leadingIcon: Icon.Error,
      leadingIconColorKey: SVGColorKey.errorIcon,
      closeOnSelect: true,
      onClick: () => onGoTo(problem),
    })),
  ];
}

export function DeployButton({
  problems,
  disabled,
  onDeploy,
  onGoTo,
}: {
  problems: readonly DeployProblem[];
  disabled: boolean;
  onDeploy: () => void;
  onGoTo: (problem: DeployProblem) => void;
}) {
  if (problems.length === 0) {
    return (
      <ButtonMediumPrimaryLeadingIcon
        text="Deploy pipeline"
        icon={Icon.Rocket}
        disabled={disabled}
        onClick={onDeploy}
        testId="studio-deploy"
      />
    );
  }
  if (onlyChecking(problems)) {
    // The disabled button swallows pointer events, so the tooltip trigger
    // wraps it (same as the toolbar's blocked Run). Every pending check
    // shares the one tooltip — services and lint can both be settling.
    return (
      <Tooltip text={problems.map((p) => p.label).join(" · ")} side="bottom">
        <ButtonMediumPrimaryLeadingIcon
          text="Deploy pipeline"
          icon={Icon.Rocket}
          disabled
          onClick={onDeploy}
          testId="studio-deploy"
        />
      </Tooltip>
    );
  }
  return (
    <Group data-testid="studio-deploy-group">
      <DeployHalf>
        <ButtonMediumPrimaryLeadingIcon
          text="Deploy pipeline"
          icon={Icon.Rocket}
          // Nothing deploys while problems remain.
          disabled
          onClick={onDeploy}
          testId="studio-deploy"
        />
      </DeployHalf>
      <GenericDropdown
        dropdownItems={problemItems(problems, onGoTo)}
        width={{ strategy: "fixed", size: MENU_WIDTH }}
        placement="bottom-end"
      >
        <GenericDropdownContext.Consumer>
          {({ open }) => (
            <UnresolvedTrigger open={open} count={problems.length} />
          )}
        </GenericDropdownContext.Consumer>
      </GenericDropdown>
    </Group>
  );
}

// "Unresolved (N)": error icon, count, chevron — Button/M metrics with
// the status/error border.
function UnresolvedTrigger({ open, count }: { open: boolean; count: number }) {
  const theme = useTheme();
  return (
    <UnresolvedButton
      type="button"
      $active={open}
      aria-expanded={open}
      data-testid="studio-unresolved"
    >
      <IconSVG
        icon={Icon.Error}
        size={16}
        colors={{ default: theme.statusError }}
      />
      Unresolved ({count})
      <IconSVG
        icon={Icon.BaseChevron}
        size={10}
        svgColorKey={SVGColorKey.semanticTextBody}
      />
    </UnresolvedButton>
  );
}

const Group = styled.div`
  display: inline-flex;
  align-items: stretch;
`;

// The pair shares one corner radius: Deploy keeps its left corners and
// sits 1px over the trigger (z 2 / z 1 in the design), so the trigger's
// error border starts at the seam.
const DeployHalf = styled.span`
  position: relative;
  z-index: 2;
  display: inline-flex;
  margin-right: -1px;

  button {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
`;

const triggerBase = css`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: ${BUTTON.gap}px;
  height: ${BUTTON.m.size}px;
  padding: 0 ${BUTTON.m.paddingw}px;
  border-radius: 0 ${BUTTON.corner}px ${BUTTON.corner}px 0;
  font-size: ${SINGLE_LINE.default.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.default.medium.fontWeight};
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
`;

const UnresolvedButton = styled.button<{ $active?: boolean }>`
  ${triggerBase};
  position: relative;
  z-index: 1;
  color: ${(p) => p.theme.semanticTextHeading};
  background: ${(p) =>
    p.$active
      ? p.theme.buttonSecondaryActiveBackground
      : p.theme.semanticAppBackground};
  border: 1px solid ${(p) => p.theme.statusError};

  &:hover {
    background: ${(p) => p.theme.buttonSecondaryHoverBackground};
  }
`;
